import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router";
import { useLoader } from "../../../../../../context";
import { getDoorsList } from "../../../../../../Service/Api/door/getList";
import arrayToObject from "../../../../../../utils/arrayToObject";

const LIMIT = 25;

const useDoorsList = (refreshData = null) => {
  const { campusId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const [availableDoorsList, setAvailableDoorsList] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  // Reset when campus changes
  useEffect(() => {
    setAvailableDoorsList({});
    setPage(1);
    setHasMore(true);
    setError(null);
  }, [campusId, refreshData]);

  const fetchDrones = useCallback(async () => {
    if (!campusId || !hasMore || isFetching) return;

    setIsFetching(true);
    page === 1 && showLoader();

    try {
      const res = await getDoorsList({
        campus_id: campusId,
        unassigned: true,
        limit: LIMIT,
        page,
      });

      const rows = res?.rowData || [];

      setAvailableDoorsList((prev) => {
        const obj = arrayToObject(rows, "device_id", { isUsed: false });
        const newList = { ...prev, ...obj };
        return newList;
      });

      // 🔑 THIS controls auto-pagination
      if (rows.length < LIMIT) {
        setHasMore(false); // stop
      } else {
        setPage((p) => p + 1); // fetch next page
      }
    } catch (err) {
      setError(err);
      setHasMore(false);
    } finally {
      setIsFetching(false);
      hideLoader();
    }
  }, [campusId, page, hasMore, isFetching]);

  // 🔁 Auto-fetch until hasMore = false
  useEffect(() => {
    fetchDrones();
  }, [fetchDrones]);

  const setAvailableDoor = useCallback(
    (doorId, status = false) => {
      setAvailableDoorsList((prev) => ({
        ...prev,
        [doorId]: { ...prev[doorId], isUsed: status },
      }));
    },
    [setAvailableDoorsList],
  );

  return {
    hasMoreDoorsList: hasMore,
    isFetching,
    error,
    availableDoorsList,
    setAvailableDoor,
    showAutomaticDoors: Object.values(availableDoorsList).length,
  };
};

export default useDoorsList;
